import { memo, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { HStack, Image, Heading, Text, Tooltip } from '@library';
import type { Product } from '../../types';
import useTabletView from '../../../core/hooks/dom/useTabletView';
import BrandImage from '../../../brand/components/BrandImage';
import { AppContext } from '../../../core/context';
import styles from './ProductDetails.module.scss';
import ProductHeader from './ProductHeader';

const SummaryHeader = ({
    product,
    selectedSKU,
}: {
    product: Product;
    selectedSKU?: string;
}) => {
    const isTabletView = useTabletView();
    const { isInMobileView } = useContext(AppContext);
    const { name, summary, manufacturer, manufacturerLogo } = product;
    const skuLabel = !selectedSKU ? (
        <FormattedMessage id="please make a choice" />
    ) : (
        selectedSKU
    );

    return !isTabletView ? (
        <>
            <HStack>
                {manufacturerLogo && (
                    <div className={styles.manufacturerLogo}>
                        <BrandImage
                            url={manufacturerLogo}
                            size="100px"
                            aspectRatio={1 / 2}
                            alt={manufacturer}
                        />
                    </div>
                )}
            </HStack>

            <div>
                <div className={styles.itemNo}>
                    <Text primary>
                        <span className={styles.itemNoTitle}>
                            <FormattedMessage id="Common ItemNumber" />:
                        </span>
                        &nbsp;
                        <span data-test-class="productDetailsSku">
                            {skuLabel}
                        </span>
                    </Text>
                </div>
                <Heading h3>
                    <span className={styles.productName}>{name}</span>
                </Heading>
                <div className={styles.productSummary}>
                    <Text primary light>
                        <span
                            /* eslint-disable-next-line react/no-danger */
                            dangerouslySetInnerHTML={{
                                __html: summary ?? '',
                            }}
                        />
                    </Text>
                </div>
            </div>
        </>
    ) : (
        <>
            {!isInMobileView && (
                <ProductHeader product={product} selectedSku={selectedSKU} />
            )}
            <div className={styles.productSummary}>
                <Text primary light>
                    <span
                        /* eslint-disable-next-line react/no-danger */
                        dangerouslySetInnerHTML={{
                            __html: summary ?? '',
                        }}
                    />
                </Text>
            </div>
        </>
    );
};

const ProductDetailsSummary = ({
    product,
    selectedSKU,
}: {
    product: Product;
    selectedSKU?: string;
}) => {
    const { pictograms } = product;

    return (
        <div>
            <SummaryHeader product={product} selectedSKU={selectedSKU} />
            {pictograms && pictograms.length > 0 && (
                <div className={styles.pictogramsContainer}>
                    <HStack gap="small">
                        {pictograms.map(
                            ({ url, label, renditionUrl, information }) => (
                                <Tooltip
                                    content={<div>{information}</div>}
                                    placement="top"
                                    showArrow
                                    key={url}
                                >
                                    <div
                                        className={
                                            styles.pictogramItemContainer
                                        }
                                    >
                                        <Image
                                            src={renditionUrl || url}
                                            alt={label || 'pictogram'}
                                        />
                                    </div>
                                </Tooltip>
                            )
                        )}
                    </HStack>
                </div>
            )}
        </div>
    );
};

export default memo(ProductDetailsSummary);
